/**
 * JobStatusStore
 * 
 * 
 */

import type { JobStatusModel } from "@/models/JobStatusModel";
import type { Ref } from "vue";

import { defineStore } from "pinia";
import { ref } from "vue";
import Api from "@/services/ApiService";
import Cache from "@/services/Cache";
import Events from "@/services/Emitter";

const jobStatusStore = () =>
{
    const CACHE_KEY = "store_jobStatus_data";
    const CACHE_TTL = 60 * 60 * 1000; // 1 hour in milliseconds
    const list = ref<Record<number, JobStatusModel>>({});
    const isInitialized = ref<boolean>(false);
    const lastLoadTime = ref<number>(0);
    let initPromise: Promise<void> | null = null;

    const isEmpty = ():boolean =>
    {
        return Object.keys(list.value).length === 0;
    }

    const init = async (): Promise<void> => {
        // Check if cache is still valid
        const now = Date.now();
        if (isInitialized.value && (now - lastLoadTime.value) < CACHE_TTL) {
            return; // Cache is still valid
        }
        
        // If initialization is in progress, return the existing promise
        if (initPromise) return initPromise;
        
        // Start new initialization
        initPromise = (async () => {
            try {
                // Check cache first (only if not expired)
                if (!isInitialized.value) {
                    const cached = await Cache.get(CACHE_KEY);
                    if (cached && typeof cached === 'object' && Object.keys(cached).length > 0) {
                        list.value = cached;
                        isInitialized.value = true;
                        lastLoadTime.value = now;
                        return;
                    }
                }
                
                // Load from server if cache is empty or expired
                await reload();
            } finally {
                // Clear the promise when done (success or failure)
                initPromise = null;
            }
        })();
        
        return initPromise;
    };
    
    const reload = async (runInBackground:boolean = false)
        :Promise<Ref<Record<number, JobStatusModel>>> =>
    {
        let tmp = await Api.loadJobStatus(runInBackground);
        let rec = {} as Record<number, JobStatusModel>;
        for (let i:number = 0; i < tmp.length; i++)
        {
            rec[tmp[i].Id] = tmp[i];
        }
        list.value = rec;
        await Cache.set(CACHE_KEY, list.value);
        isInitialized.value = true;
        lastLoadTime.value = Date.now();
        return list;
    }

    const refresh = async (): Promise<void> => {
        await reload();
    };

    const load = async (runInBackground:boolean = false)
        :Promise<Ref<Record<number, JobStatusModel>>> =>
    {
        await init();
        return list;
    }
    
    const getById = async (status:number):Promise<JobStatusModel | null> =>
    {
        await init();
        
        if (list.value[status]) {
            return list.value[status];
        }
        
        return null;
    }

    // register global events
    Events.on("JobStatusStore::Reload", async () =>
    {
        console.debug("JobStatusStore::Reload called...");
        await reload();
    });

    Events.on("refresh:jobStatusStore", async () => {
        console.debug("refresh:jobStatusStore event received");
        await refresh();
    });

    return { load, reload, refresh, getById, list };
};

export const useJobStatusStore = defineStore("jobStatus", jobStatusStore);